import z from 'zod';

import {
    PlateUnorderedList,
    UnityUnorderedList,
    UnorderedListCodec,
    type UnorderedListElement,
} from './List';
import { CURRENT_VERSION, ElementId, emptyChildren, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES } from '../constants';

export const PlateSummaryListSummary = z.object({
    type: z.literal(ELEMENT_TYPES.SUMMARY_LIST_SUMMARY),
    data: z.object({}).optional(),
    children: z.tuple([PlateUnorderedList]),
});
export type SummaryListSummaryElement = z.infer<typeof PlateSummaryListSummary>;

export const UnitySummaryList = z.object({
    type: z.literal(ELEMENT_TYPES.SUMMARY_LIST),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    summary: UnityUnorderedList.nullable(),
});
export type UnitySummaryList = z.infer<typeof UnitySummaryList>;

export const PlateSummaryList = z.object({
    type: z.literal(ELEMENT_TYPES.SUMMARY_LIST),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.tuple([PlateSummaryListSummary]),
});
export type SummaryListElement = z.infer<typeof PlateSummaryList>;

export const SummaryCodec = z.codec(UnitySummaryList, PlateSummaryList, {
    decode: (value) => {
        const deserializedList: UnorderedListElement = value.summary
            ? UnorderedListCodec.decode(value.summary)
            : {
                  type: ELEMENT_TYPES.UNORDERED_LIST,
                  id: crypto.randomUUID(),
                  children: [
                      {
                          type: ELEMENT_TYPES.LIST_ITEM,
                          id: crypto.randomUUID(),
                          children: [
                              {
                                  type: ELEMENT_TYPES.LIST_ITEM_CONTENT,
                                  children: emptyChildren,
                              },
                          ],
                      },
                  ],
              };

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
            children: [
                {
                    type: ELEMENT_TYPES.SUMMARY_LIST_SUMMARY,
                    children: [deserializedList],
                },
            ] satisfies [SummaryListSummaryElement],
        };
    },
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        summary: UnorderedListCodec.encode(value.children[0].children[0]),
        ...value.data,
    }),
});
